import React, { useEffect, useState, useRef } from "react";
import { useCompatRouter as useRouter } from "@/lib/compat-router/context";
import FormInput from "../input";
import DropdownMenu from "../dropdown";
import Button from "../button";
import { getCitiesByState, nigeriaStates } from "@/utils/location";
import { Search } from "@/components/bnp-icons";
import { Controller } from "react-hook-form";
import SearchableDropdown from "../search-dropdown";
import PlacesAutocomplete from "@/components/pages/home/search/LocationDropdown";
import { useLoadScript, Autocomplete } from "@react-google-maps/api";
import { capitalizeFirstLetter } from "@/utils/capitalizeFirstLetter";
import { PROPERTY_TYPE_LABELS } from "@/components/pages/user/dashboard/properties/config/propertyCategories";

export function getPropertyType(object: any, value: any) {
  return Object.keys(object).find((key) => object[key] === value);
}
const PropertyFilters = () => {
  const router = useRouter();
  const [cityOptions, setCityOptions] = useState<string[]>([]);

  const {
    location,
    shared,
    serviced,
    addedToSite,
    state,
    city,
    bedroomCount,
    bathroomCount,
    listingType,
    realtorId,
    furnished,
    propertyType,
  } = router.query;
  const [selectedState, setSelectedState] = useState<any>(state || null);

  const [filters, setFilters] = React.useState({
    location: location || "Lagos",
    serviced: serviced ? "Yes" : "No",
    shared: shared ? "Yes" : "No",
    addedToSite: addedToSite || "",
    state: state || "",
    city: city || "",
    bedroomCount: bedroomCount || "",
    bathroomCount: bathroomCount || "",
    listingType: listingType || "",
    propertyType: propertyType || "",
    minPrice: "",
    maxPrice: "",
    furnished: furnished ? "Yes" : "No",
  });

  useEffect(() => {
    if (selectedState) {
      const cities = getCitiesByState(selectedState);
      setCityOptions(cities);
    } else {
      setCityOptions([]);
    }
  }, [selectedState]);

  const handleFilterChange = (key: string, value: string | boolean) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const applyFilters = () => {
    const queryParams = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== "Any") {
        if (value === "Yes") {
          value = "true";
        } else if (value === "No") {
          value = "false";
        }
        queryParams.append(key, value.toString());
      }
    });

    if (realtorId) {
      queryParams.append("realtorId", realtorId.toString());
    }

    router.push({
      pathname: router.pathname,
      query: queryParams.toString(),
    });
  };

  const clearFilters = () => {
    setFilters({
      shared: "Any",
      addedToSite: "Any",
      serviced: "Any",
      location: "Any",
      state: "Any",
      city: "Any",
      bedroomCount: "Any",
      bathroomCount: "Any",
      listingType: "Any",
      propertyType: "Any",
      minPrice: "Any",
      maxPrice: "Any",
      furnished: "Any",
    });

    if (realtorId) {
      router.push({
        pathname: router.pathname,
        query: { realtorId },
      });
    } else {
      router.push(router.pathname);
    }
  };

  const libraries: "places"[] = ["places"];

  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_PLACES_API_KEY as string,
    libraries,
  });

  const autocompleteOptions = {
    componentRestrictions: { country: "NG" },
    fields: ["name"],
    types: ["(cities)"],
  };

  return (
    <div className="w-full bg-white border rounded-lg p-4 shadow-md">
      <h2 className="text-lg my-4 font-medium text-main-900">Search Filters</h2>
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4">
          <div className="relative">
            {isLoaded ? (
              <Autocomplete
                options={autocompleteOptions}
                onLoad={(autocomplete) => {
                  autocompleteRef.current = autocomplete;
                }}
                onPlaceChanged={() => {
                  const place = autocompleteRef.current?.getPlace();
                  if (place?.name) {
                    handleFilterChange("location", place?.name);
                  }
                }}
              >
                <FormInput
                  type="text"
                  name="location"
                  value={filters.location}
                  label="Location"
                  placeholder="Enter city"
                  className="w-[100%]"
                  onChange={(e) =>
                    handleFilterChange("location", e.target.value)
                  }
                />
              </Autocomplete>
            ) : (
              <div>
                <FormInput
                  hero
                  type="text"
                  name="location"
                  value={filters.location}
                  label="Location"
                  placeholder="Enter city"
                  className="w-[100%]"
                  onChange={(e) =>
                    handleFilterChange("location", e.target.valuetoLowerCase())
                  }
                  disabled={!isLoaded}
                />
                {!isLoaded && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="flex flex-col gap-1">
            <p className="text-sm font-medium text-main-900">Listing Type</p>
            <DropdownMenu
              name="listingType"
              value={
                filters.listingType
                  ? capitalizeFirstLetter(filters.listingType as string)
                  : "Any"
              }
              onChange={(e) =>
                handleFilterChange(
                  "listingType",
                  e.target.value === "Any" ? "" : e.target.value.toLowerCase(),
                )
              }
              options={["Any", "Rent", "Sale", "Shortlet", "Land"]}
            />
          </div>
          <div className="flex flex-col gap-1">
            <p className="text-sm font-medium text-main-900">Property Type</p>
            <DropdownMenu
              name="propertyType"
              value={
                filters.propertyType
                  ? PROPERTY_TYPE_LABELS[
                      filters.propertyType as keyof typeof PROPERTY_TYPE_LABELS
                    ] || filters.propertyType
                  : "Any"
              }
              onChange={(e) => {
                const selectedLabel = e.target.value;
                const apiValue =
                  selectedLabel === "Any"
                    ? ""
                    : Object.entries(PROPERTY_TYPE_LABELS).find(
                        ([_, label]) => label === selectedLabel,
                      )?.[0] || "";
                handleFilterChange("propertyType", apiValue);
              }}
              options={["Any", ...Object.values(PROPERTY_TYPE_LABELS)]}
            />
          </div>
          <div className="flex flex-col gap-1">
            <p className="text-sm font-medium text-main-900">State</p>
            <SearchableDropdown
              name={"state"}
              options={["Any", ...nigeriaStates]}
              value={filters.state || "Any"}
              onChange={(e) => {
                handleFilterChange(
                  "state",
                  e.target.value === "Any" ? "" : e.target.value,
                );
                setSelectedState(
                  e.target.value === "Any" ? null : e.target.value,
                );
                handleFilterChange("city", "Any");
              }}
            />
          </div>
          <div className="flex flex-col gap-1">
            <p className="text-sm font-medium text-main-900">City</p>
            <SearchableDropdown
              name={"city"}
              value={filters.city || "Any"}
              options={["Any", ...cityOptions]}
              onChange={(e) =>
                handleFilterChange(
                  "city",
                  e.target.value === "Any" ? "" : e.target.value,
                )
              }
              disabled={!selectedState}
            />
          </div>
          <div className="flex flex-col gap-1">
            <p className="text-sm font-medium text-main-900">Bedrooms</p>
            <DropdownMenu
              name={"bedroomCount"}
              options={[
                "1",
                "2",
                "3",
                "4",
                "5",
                "6",
                "7",
                "8",
                "9",
                "10",
                "10+",
              ]}
              value={filters.bedroomCount}
              onChange={(e) =>
                handleFilterChange("bedroomCount", e.target.value)
              }
            />
          </div>
          <div className="flex flex-col gap-1">
            <p className="text-sm font-medium text-main-900">Bathrooms</p>
            <DropdownMenu
              name={"bathroomCount"}
              options={[
                "1",
                "2",
                "3",
                "4",
                "5",
                "6",
                "7",
                "8",
                "9",
                "10",
                "10+",
              ]}
              value={filters.bathroomCount}
              onChange={(e) =>
                handleFilterChange("bathroomCount", e.target.value)
              }
            />
          </div>
          <div className="min-w-[140px] flex flex-col gap-1">
            <p className="text-sm font-medium text-main-900">Serviced</p>
            <DropdownMenu
              name="serviced"
              options={["Yes", "No"]}
              onChange={(e) => handleFilterChange("serviced", e.target.value)}
              value={filters.serviced}
            />
          </div>

          <div className="min-w-[140px] flex flex-col gap-1">
            <p className="text-sm font-medium text-main-900">Shared</p>
            <DropdownMenu
              name="shared"
              options={["Yes", "No"]}
              onChange={(e) => handleFilterChange("shared", e.target.value)}
              value={filters.shared}
            />
          </div>

          <div className="min-w-[140px] flex flex-col gap-1">
            <p className="text-sm font-medium text-main-900">Furnished</p>
            <DropdownMenu
              name="furnished"
              options={["Yes", "No"]}
              onChange={(e) => handleFilterChange("furnished", e.target.value)}
              value={filters.furnished}
            />
          </div>

          <div className="min-w-[140px] flex flex-col gap-1">
            <p className="text-sm font-medium text-main-900">Added to site</p>
            <DropdownMenu
              name="addedToSite"
              options={["Last 24 hours", "Last week", "Last month"]}
              onChange={(e) =>
                handleFilterChange("addedToSite", e.target.value)
              }
              value={filters.addedToSite}
            />
          </div>
          <div className="grid grid-cols-1 gap-2">
            <div className="w-fit space-y-2 lg:w-[100%] ">
              <p className="text-sm font-medium text-main-900">Min. Budget</p>
              <FormInput
                name="minPrice"
                type="number"
                placeholder="Enter min budget"
                value={filters.minPrice}
                onChange={(e) => handleFilterChange("minPrice", e.target.value)}
              />
            </div>
            <div className="w-fit space-y-2 lg:w-[100%]">
              <p className="text-sm font-medium text-main-900">Max. Budget</p>
              <FormInput
                name="maxPrice"
                type="number"
                placeholder="Enter max budget"
                value={filters.maxPrice}
                onChange={(e) => handleFilterChange("maxPrice", e.target.value)}
              />
            </div>
          </div>

          <div className="flex justify-center items-center gap-4 mt-6">
            <Button
              onClick={clearFilters}
              className="mt-4 lg:mt-6 bg-white font-sans text-bnp-dark border-[1.4px] border-bnp-dark text-sm font-medium w-[300px] lg:w-[137px] h-[52px] rounded-lg"
            >
              <div className="flex gap-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-filter-x"
                >
                  <path d="M13.013 3H2l8 9.46V19l4 2v-8.54l.9-1.055" />
                  <path d="m22 3-5 5" />
                  <path d="m17 3 5 5" />
                </svg>
                <p>Clear</p>
              </div>
            </Button>
            <Button
              onClick={applyFilters}
              className="mt-4 lg:mt-6 bg-bnp-dark font-sans text-white text-sm font-medium w-[300px] lg:w-[137px] h-[52px] rounded-lg"
            >
              <div className="flex gap-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-filter"
                >
                  <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                </svg>
                <p>Apply</p>
              </div>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyFilters;
